import AttendanceSheetStatusIcon from '../../components/IconeStatus';
import Links from '@/shared/constants/navegacao';
import MyContext from '@/shared/contexts/AppContext';
import { AttendanceSheetPdf } from '../../components/AttendanceSheetPDF';
import { Button } from 'react-bootstrap';
import { Column, DataTable } from '@/shared/components/tables/DataTable';
import { FaEye, FaRegFilePdf, FaTrash, FaUserTie, FaSignature } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import {
  ListAllIntershipsOfTheMonthResponseDto,
  AttendanceSheetResponseDto,
} from '@/api.types';
import { MdOutgoingMail } from 'react-icons/md';
import { TiDocumentAdd } from 'react-icons/ti';
import { exportFromJsxToPdf } from '@/shared/utils/pdf';
import { deleteAttendanceSheet, readByIdAttendanceSheet } from '../../../attendance-sheet.service';
import { toast } from 'react-toastify';
import { useContext } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

function formatarReferenciaFolha(ano: number, mes: number): string {
  const texto = format(new Date(ano, mes - 1, 1), 'MMMM / yyyy', { locale: ptBR });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

interface Props {
  registros: [ListAllIntershipsOfTheMonthResponseDto[], number];
  tableState: any;
  handleBuscar: () => void;
  setSelectedItem: (item: ListAllIntershipsOfTheMonthResponseDto) => void;
  setShowModalCriarFolha: (v: boolean) => void;
  setFolhaSelecionada: (row: ListAllIntershipsOfTheMonthResponseDto) => void;
  setShowModalResponsavel: (v: boolean) => void;
  setShowModalNotificacaoIndividual: (v: boolean) => void;
  setFolhaParaAssinaturas: (folha: AttendanceSheetResponseDto | null) => void;
  setShowModalAssinaturas: (v: boolean) => void;
  permissaoExcluirFolhas: boolean;
  onVisibleColumnsChange?: (visibleColumnKeys: string[]) => void;
  showReferenciaColumn?: boolean;
}

export function AttendanceSheetsTable({
  registros,
  tableState,
  handleBuscar,
  setSelectedItem,
  setShowModalCriarFolha,
  setFolhaSelecionada,
  setShowModalResponsavel,
  setShowModalNotificacaoIndividual,
  setFolhaParaAssinaturas,
  setShowModalAssinaturas,
  permissaoExcluirFolhas,
  onVisibleColumnsChange,
  showReferenciaColumn = false,
}: Props) {
  const context = useContext(MyContext);
  if (!context)
    throw new Error('Contexto deve ser usado dentro de um Provedor.');

  const handlePrintAttendanceSheet = async (folhaId: string) => {
    try {
      const folha = await readByIdAttendanceSheet(folhaId);
      exportFromJsxToPdf(<AttendanceSheetPdf folha={folha} />);
    } catch (error) {
      toast.error(String(error));
    }
  };

  const handleShowAssinaturas = async (folhaId: string) => {
    try {
      const folha = await readByIdAttendanceSheet(folhaId);
      setFolhaParaAssinaturas(folha);
      setShowModalAssinaturas(true);
    } catch (error) {
      toast.error(String(error));
    }
  };

  const handleDeleteAttendanceSheet = async (folhaId: string) => {
    const confirmed = window.confirm('Deseja realmente excluir esta folha de frequência?');
    if (!confirmed) return;

    try {
      const deleted = await deleteAttendanceSheet(folhaId);
      if (deleted) {
        toast.success('Folha de frequência excluída com sucesso.');
        handleBuscar();
        return;
      }
      toast.error('Não foi possível excluir a folha de frequência.');
    } catch (error) {
      toast.error(String(error));
    }
  };

  const colunas: Column<ListAllIntershipsOfTheMonthResponseDto>[] = [
    {
      key: 'referencia',
      header: 'Referência',
      invisible: !showReferenciaColumn,
      render: (row) => formatarReferenciaFolha(row.ano, row.mes),
    },
    { key: 'estagiario.cpf', header: 'CPF', invisible: true },
    { key: 'estagiario.nome', header: 'Estagiário(a)' },
    { key: 'estagio.unidade', header: 'Unidade/Setor' },
    { key: 'responsavel.nome', header: 'Responsável' },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <div className="text-center">
          {row.folha ? (
            <Link to={`${Links.Folha_Frequencia_Editar}/${row.folha.id}`}>
              <AttendanceSheetStatusIcon status={row.folha.status} />
            </Link>
          ) : (
            context.state.username && (
              <Button
                className="btn btn-link link-secondary"
                onClick={() => {
                  setSelectedItem(row);
                  setShowModalCriarFolha(true);
                }}
                title="Criar folha de frequência."
              >
                <TiDocumentAdd size={25} color="#0cb389ff" />
              </Button>
            )
          )}
        </div>
      ),
    },
    {
      key: 'folha.editadoEm',
      header: 'Editado Em',
      render: (row) =>
        row.folha
          ? new Date(row.folha.editadoEm).toLocaleDateString('pt-BR', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })
          : '',
    },
    {
      key: 'acoes',
      header: 'Ações',
      render: (row) =>
        row.folha && (
          <div className="d-flex align-items-center">
            <Link
              className="link-secondary me-3"
              to={`${Links.Folha_Frequencia_Editar}/${row.folha.id}`}
              title="Visualizar a folha."
            >
              <FaEye size={24} color="#0daaaf" />
            </Link>
            <Button
              className="btn btn-link link-secondary"
              onClick={() => handlePrintAttendanceSheet(row.folha.id)}
              title="Gerar PDF da folha."
            >
              <FaRegFilePdf size={22} color="#ff4444" />
            </Button>
            {context.state.username && (
              <>
                <Button
                  className="btn btn-link link-secondary"
                  onClick={() => {
                    setFolhaSelecionada(row);
                    setShowModalResponsavel(true);
                  }}
                  title="Definir responsável."
                >
                  <FaUserTie size={18} color="#555555" />
                </Button>
                <Button
                  className="btn btn-link link-secondary"
                  onClick={() => {
                    setFolhaSelecionada(row);
                    setShowModalNotificacaoIndividual(true);
                  }}
                  title="Notificar."
                >
                  <MdOutgoingMail size={24} color="#449623ff" />
                </Button>
                <Button
                  className="btn btn-link link-secondary"
                  onClick={() => handleShowAssinaturas(row.folha.id)}
                  title="Ver assinaturas e justificativas."
                >
                  <FaSignature size={20} color="#6c757d" />
                </Button>
              </>
            )}
          </div>
        ),
    },
    {
      key: 'acoes+',
      header: 'Ações+',
      invisible: true,
      render: (row) =>
        <div className="d-flex align-items-center">
          <Link className="link-secondary" to={`${Links.Folha_Frequencia_Index}/${row.estagiario.id}`} title="Ver folhas de frequência" target='_blank' >
            <FaRegFilePdf size={20} color="#0044ff" />
          </Link>
          {row.folha && permissaoExcluirFolhas ? (
            <Button
              className="btn btn-link link-secondary"
              onClick={() => handleDeleteAttendanceSheet(row.folha!.id)}
              title="Excluir folha (somente sem dias preenchidos)."
            >
              <FaTrash size={18} color="#dc3545" />
            </Button>
          ) : null}
        </div>
    },
  ];

  return (
    <DataTable
      columns={colunas}
      data={registros[0]}
      initialSortKey="estagiario.nome"
      showFilter={false}
      onChange={handleBuscar}
      serverSide
      barras
      states={tableState}
      totalRecords={registros[1]}
      onVisibleColumnsChange={onVisibleColumnsChange}
    />
  );
}
